// Streaming chat over POST /api/chat. CloudFront signs requests to the chat function (OAC), which
// needs two things from the browser: the SHA-256 of the body in x-amz-content-sha256, and the
// user's token somewhere other than Authorization (OAC overwrites it): x-askhr-token.

import { apiToken } from "../auth/msal.js";

export interface Source {
  n: number;
  docId: string;
  title: string;
  label: string;
  url: string;
}

export type { Audience, LocationSummary, WorkLocation } from "@ask-hr/core/sources";
import type { LocationSummary } from "@ask-hr/core/sources";

export type ChatLine =
  | { t: "meta"; conversationId: string; location: LocationSummary; corpusVersion: number | null }
  | { t: "text"; v: string }
  | { t: "source"; source: Source }
  | { t: "done"; conversationId: string; messageId: string }
  | { t: "needs_location"; location: LocationSummary; question: string }
  | { t: "error"; code: string; message: string };

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function postChat(
  body: { conversationId?: string; message: string },
  onLine: (line: ChatLine) => void,
  signal?: AbortSignal,
): Promise<void> {
  const json = JSON.stringify(body);
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-amz-content-sha256": await sha256Hex(json),
      "x-askhr-token": await apiToken(),
    },
    body: json,
    ...(signal ? { signal } : {}),
  });
  if (!res.body) throw new Error(`HTTP ${res.status}`);

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let nl: number;
    while ((nl = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (line) onLine(JSON.parse(line) as ChatLine);
    }
  }
  if (buffer.trim()) onLine(JSON.parse(buffer) as ChatLine);
  if (!res.ok && res.status !== 401) throw new Error(`HTTP ${res.status}`);
}
